import { Object3D, TextComponent } from '@wonderlandengine/api';
import { WonderlandWebsocketEvent, networkManager } from '@wonderlandcloud/client';
import { NetworkedComponent } from '@wonderlandcloud/client';
import { NetworkListenVideoTexture } from '../networklisten-video-texture.js';
import { CopyRotation, CopyPosition } from '../copy-rotation.js';
import { setComponentsActive } from '../utils/activate-children.js';
import { AudioSource } from '@wonderlandengine/spatial-audio';
import { SpeakerIndicator, SpeakerState } from '../speaker-indicator.js';
import { Events } from '../network-events.js';
import Logger from '../utils/logger.js';
import { UserMessaging } from '../utils/user-messaging.js';
import { NetworkedGrabbable } from '../networked-grabbable.js';
import { EToyDefinition } from '../etoy/etoy-types.js';
import { Emitter } from '@wonderlandengine/api';
import { SceneController } from '../scene-controller.js';
import { AvatarState, EventsClient, ProstheticState } from '../events-client.js';
import type { SharedUrlVideoShare } from '../media/url-video-sync.js';

const MODULE = 'EventsHandler';

export interface EventHandlerContext {
    remoteUsers: Map<number | string, any>;
    remoteUsernames: Map<number, string>;
    remoteUserJoinData: Map<number, any>;
    headlessmode: boolean;
    playerPrototype: Object3D;
    playerUserId: number;
    serverAssignedUserId: number;
    _serverNetworkId: number;
    radioObject?: Object3D;
    toys: Map<number, any>;
    toyInstances: Map<number, Object3D>;
    networkedGrabbables: Map<number, NetworkedGrabbable>;
    vidrecPeerConnections: Map<number, RTCPeerConnection>;
    vidsharePeerConnections: Map<number, RTCPeerConnection>;
    vidrecCandidatesMap: Map<number, RTCIceCandidateInit[]>;
    vidshareCandidatesMap: Map<number, RTCIceCandidateInit[]>;
    sharingVideo: boolean;
    masterSharerId: number;
    activeVideoStreams: Map<number, MediaStream>;
    activeUrlVideoShare: SharedUrlVideoShare | null;
    onUserJoinEvent: Emitter<[number]>;
    onClaimMasterShareEvent: Emitter<[number]>;
    onShareStopEvent: Emitter<[number]>;
    onSyncUrlVideoStartEvent: Emitter<[{
        url: string;
        startAtMs: number;
        requestedBy: number;
        seekSeconds: number;
    }]>;
    onSyncUrlVideoPingEvent: Emitter<[{
        url: string;
        startAtMs: number;
        requestedBy: number;
        seekSeconds: number;
    }]>;
    debug: boolean;
    registerToy: (toy: EToyDefinition) => void;
    removeAllToys: (ids?: number[]) => void;
    handleVidshareOffer: (senderId: number, offer: any) => Promise<void>;
    destroyVidrecConnection: (peerId: number) => void;
    destroyVidshareConnection: (peerId: number) => void;
    applyLocalProstheticState: (state: ProstheticState) => void;
    applyRemoteProstheticState: (userId: number, state: ProstheticState, deactivatedNetworkId?: number | null) => void;
    applyAvatarState: (state: AvatarState) => void;
    applyRemoteAvatarState: (userId: number, state: AvatarState) => void;
}

export function handleNetworkEvent(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    const remoteUser = context.remoteUsers.get(e.data.id);
    switch (e.type) {
        case Events.USER_JOINED:
            handleUserJoined(context, e, remoteUser);
            break;

        case Events.USER_LEFT:
            handleUserLeft(context, e, remoteUser);
            break;

        case Events.USER_ENTER_XR:
            handleUserEnterXR(context, e, remoteUser);
            break;

        case Events.USER_EXIT_XR:
            handleUserExitXR(context, e, remoteUser);
            break;

        case Events.RADIO_TOGGLE:
            handleRadioToggle(context, e);
            break;

        case Events.OBJECT_GRAB:
            handleObjectGrab(context, e);
            break;

        case Events.TOY_CREATED:
            handleToyCreated(context, e);
            break;

        case Events.TOY_SNAPSHOT:
            handleToySnapshot(context, e);
            break;

        case Events.TOY_ALL_DELETED:
            handleToyAllDeleted(context, e);
            break;

        case Events.SCENE_SWITCH_RESPONSE:
            handleSceneSwitchResponse(e);
            break;

        case Events.SCENE_SWITCH_APPROVED:
            handleSceneSwitchApproved(context, e);
            break;

        case Events.PLAYER_SPEAK_CHANGE:
            handlePlayerSpeakChange(context, e, remoteUser);
            break;

        case Events.PLAYER_SHARING_STREAM_OFFER:
            handlePlayerSharingStreamOffer(context, e);
            break;

        case Events.PLAYER_SHARING_STREAM_OFFER_ICE:
            handlePlayerSharingStreamOfferICE(context, e);
            break;

        case Events.PLAYER_SHARING_STREAM_ANSWER_ICE:
            handlePlayerSharingStreamAnswerICE(context, e);
            break;

        case Events.PLAYER_SHARING_STREAM_ANSWER:
            handlePlayerSharingStreamAnswer(context, e);
            break;

        case Events.PLAYER_STOPPED_SHARING_STREAM:
            handlePlayerStoppedSharingStream(context, e);
            break;

        case Events.PLAYER_CLAIMING_MASTER_SHARE_ROLE:
            handlePlayerClaimingMasterShareRole(context, e);
            break;

        case Events.PLAYER_RELEASING_MASTER_SHARE_ROLE:
            handlePlayerReleasingMasterShareRole(context, e);
            break;

        case Events.PLAYER_SYNC_URL_VIDEO_START:
            handlePlayerSyncUrlVideoStart(context, e);
            break;

        case Events.PLAYER_SYNC_URL_VIDEO_PING:
            handlePlayerSyncUrlVideoPing(context, e);
            break;

        case Events.SERVER_PING:
            handleServerPing(e);
            break;

        case Events.SET_ID:
            handleSetId(context, e);
            break;

        case Events.PROSTHETIC_STATE_UPDATE:
            handleProstheticStateUpdate(context, e);
            break;

        case Events.AVATAR_STATE_UPDATE:
            handleAvatarStateUpdate(context, e);
            break;

        case Events.MARKETPLACE_TIP_RECEIVED:
            handleMarketplaceTipReceived(e);
            break;

        default:
            if (context.debug) {
                Logger.debug('Unknown event:', MODULE, e);
            }
    }
}

function handleUserJoined(context: EventHandlerContext, e: WonderlandWebsocketEvent, remoteUser: any): void {
    context.remoteUsernames.set(e.data.id, e.data.name);

    // Clean up any existing (possibly destroyed) avatar for this user before re-creating.
    // This handles the re-join case where sceneRoot.destroy() may have destroyed prior clones.
    if (remoteUser) {
        Object.values(remoteUser).forEach(({ object, id }: any) => {
            if (typeof id === 'number' && id >= 0) networkManager.removeObject(id);
            if (object && !object.isDestroyed) object.destroy();
        });
        context.remoteUsers.delete(e.data.id);
    }

    let userObjects = {};

    if (!context.headlessmode && !e.data.headlessmode) {
        if (context.playerPrototype.isDestroyed) {
            Logger.error(`playerPrototype is destroyed — cannot create avatar for user ${e.data.id} (${e.data.name})`, MODULE);
            return;
        }
        const root: Object3D = context.playerPrototype.clone(null);
        if (!root) {
            Logger.error(`playerPrototype.clone(null) returned null for user ${e.data.id} (${e.data.name})`, MODULE);
            return;
        }

        const usernameObject = root.findByNameRecursive('Username')[0];
        const usernameText = usernameObject?.getComponent(TextComponent);
        if (usernameText) {
            usernameText.text = e.data.name;
        }

        const userIndicatorObject = root.findByNameRecursive('Indicator')[0];

        userObjects = {
            root: {
                object: root,
                id: -1,
            },
            userIndicatorObject: {
                object: userIndicatorObject,
                id: -1,
            },
        };

        // Register root first so avatar state can attach visuals under RemoteAvatarMods.
        context.remoteUsers.set(e.data.id, userObjects);
        context.remoteUserJoinData.set(e.data.id, e.data);

        const avatarState: AvatarState = {
            avatarId: e.data?.avatarId,
        };
        context.applyRemoteAvatarState(e.data.id, avatarState);

        const videoPlayerObjects = root.findByNameRecursive('VideoPlayer');
        const vidPlayer = videoPlayerObjects[0]?.getComponent(NetworkListenVideoTexture);
        if (vidPlayer) {
            vidPlayer.remPlayer = e.data.id;
            vidPlayer.active = true;
        }

        const remoteAvatarMods = root.findByNameRecursive('RemoteAvatarMods')[0];
        const avatarVisualRoot = (userObjects as any)?.avatarVisual?.object as Object3D | undefined;
        const head = (userObjects as any).head?.object ?? root.findByNameRecursive('Head')[0] ?? avatarVisualRoot ?? remoteAvatarMods;
        if (head) {
            head.addComponent(NetworkedComponent, {
                networkId: e.data.networkIds[0],
                mode: 'receive',
            });
            (userObjects as any).head = {
                object: head,
                id: e.data.networkIds[0],
            };
        } else {
            Logger.warn(`Head anchor not found for user ${e.data.id}; avatar may not animate correctly`, MODULE);
        }

        const leftHand = (userObjects as any).leftHand?.object ?? root.findByNameRecursive('LeftHand')[0];
        if (leftHand) {
            leftHand.addComponent(NetworkedComponent, {
                networkId: e.data.networkIds[1],
                mode: 'receive',
            });
            if (e.data.inXR) {
                leftHand.addComponent(CopyRotation, {
                    axis: 3,
                    other: leftHand,
                });
            }
            leftHand.addComponent(CopyPosition, {
                other: leftHand,
            });
            (userObjects as any).leftHand = {
                object: leftHand,
                id: e.data.networkIds[1],
            };
        } else {
            Logger.warn(`LeftHand object not found for user ${e.data.id}; left hand tracking will be missing`, MODULE);
        }

        const rightHand = (userObjects as any).rightHand?.object ?? root.findByNameRecursive('RightHand')[0];
        if (rightHand) {
            rightHand.addComponent(NetworkedComponent, {
                networkId: e.data.networkIds[2],
                mode: 'receive',
            });
            if (e.data.inXR) {
                rightHand.addComponent(CopyRotation, {
                    axis: 3,
                    other: rightHand,
                });
            }
            rightHand.addComponent(CopyPosition, {
                other: rightHand,
            });
            (userObjects as any).rightHand = {
                object: rightHand,
                id: e.data.networkIds[2],
            };
        } else {
            Logger.warn(`RightHand object not found for user ${e.data.id}; right hand tracking will be missing`, MODULE);
        }
    }

    context.remoteUsers.set(e.data.id, userObjects);
    context.remoteUserJoinData.set(e.data.id, e.data);

    const prostheticState = e.data?.prostheticState;
    if (prostheticState) {
        context.applyRemoteProstheticState(e.data.id, {
            type: prostheticState.type,
            networkId: typeof prostheticState.networkId === 'number' ? prostheticState.networkId : null,
        });
    }

    UserMessaging.instance.notify(`${e.data.name.toString()} joined the game.`);

    context.onUserJoinEvent.notify(e.data.id);
}

function handleAvatarStateUpdate(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    const userId = Number(e.data?.id);
    if (!Number.isFinite(userId) || userId <= 0) {
        Logger.warn('Invalid AVATAR_STATE_UPDATE payload: missing user id', MODULE, e.data);
        return;
    }

    const state: AvatarState = {
        avatarId: e.data?.avatarId ?? e.data?.data?.avatarId ?? e.data?.payload?.avatarId,
    };

    if (userId === context.playerUserId) {
        context.applyAvatarState(state);
        return;
    }

    const cachedJoin = context.remoteUserJoinData.get(userId);
    if (cachedJoin) {
        cachedJoin.avatarId = state.avatarId;
        context.remoteUserJoinData.set(userId, cachedJoin);
    }

    context.applyRemoteAvatarState(userId, state);
}

function handleUserLeft(context: EventHandlerContext, e: WonderlandWebsocketEvent, remoteUser: any): void {
    const userId = e.data.id;
    const hadActiveVideoStream = context.activeVideoStreams.has(userId);
    const hadVidrecConnection = context.vidrecPeerConnections.has(userId);

    if (remoteUser) {
        Object.values(remoteUser).forEach(({ object, id }: any) => {
            if (typeof id === 'number') networkManager.removeObject(id);
            if (object && !object.isDestroyed) {
                object.destroy();
            }
        });
        context.remoteUsers.delete(userId);
        context.remoteUserJoinData.delete(userId);
    }
    UserMessaging.instance.notify(`${e.data.name.toString()} left the game.`);

    context.destroyVidrecConnection(userId);
    context.destroyVidshareConnection(userId);
    context.remoteUsernames.delete(userId);

    if (hadActiveVideoStream || hadVidrecConnection) {
        context.onShareStopEvent.notify(userId);
    }
}

function setRemoteUserXRState(context: EventHandlerContext, userId: number, inXR: boolean): void {
    const cachedJoin = context.remoteUserJoinData.get(userId);
    if (cachedJoin) {
        cachedJoin.inXR = inXR;
        context.remoteUserJoinData.set(userId, cachedJoin);
    }
}

function handleUserEnterXR(context: EventHandlerContext, e: WonderlandWebsocketEvent, remoteUser: any): void {
    setRemoteUserXRState(context, e.data.id, true);
    if (remoteUser && !context.headlessmode) {
        const leftHand = remoteUser['leftHand']?.object;
        const rightHand = remoteUser['rightHand']?.object;
        if (leftHand) setComponentsActive(leftHand, true);
        if (rightHand) setComponentsActive(rightHand, true);
    }
}

function handleUserExitXR(context: EventHandlerContext, e: WonderlandWebsocketEvent, remoteUser: any): void {
    setRemoteUserXRState(context, e.data.id, false);
    if (remoteUser && !context.headlessmode) {
        const leftHand = remoteUser['leftHand']?.object;
        const rightHand = remoteUser['rightHand']?.object;
        if (leftHand) setComponentsActive(leftHand, false);
        if (rightHand) setComponentsActive(rightHand, false);
    }
}

function handleRadioToggle(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    if (context.radioObject && !context.headlessmode) {
        const radio = context.radioObject.getComponent(AudioSource);
        if (radio) {
            if (e.data && e.data.radioState) {
                radio.play();
            } else {
                radio.stop();
            }
        }
    }
}

function handleObjectGrab(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    if (!context.headlessmode) {
        Logger.info('Received OBJECT_GRAB event:' + e.data, MODULE);
        const objectId = e.data.objectId;
        const playerId = e.data.playerId;
        const grabbed = e.data.grabbed;

        const toy = context.toys.get(objectId);
        if (toy) {
            toy.currentGrabbedBy = grabbed ? playerId : null;
            context.toys.set(objectId, toy);
            if (!grabbed && toy.physxOwner === context.playerUserId) {
                const toyInst = context.toyInstances.get(objectId);
            }
        }

        const grabbableComponent = context.networkedGrabbables.get(objectId);
        if (grabbableComponent) {
            grabbableComponent.onRemoteGrabChange(playerId, grabbed);

            const action = grabbed ? 'grabbed' : 'released';
            const playerName = context.remoteUsernames.get(playerId) || `Player ${playerId}`;
            Logger.debug(`${playerName} ${action} object with networkId ${objectId}`, MODULE);
        } else {
            Logger.warn(`Received OBJECT_GRAB event for unknown object networkId: ${objectId}`, MODULE);
        }
    }
}

function handleToyCreated(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    if (!context.headlessmode) {
        const toy = e.data;
        if (!toy || typeof toy.networkId !== 'number') {
            Logger.warn('TOY_CREATED missing networkId', MODULE);
            return;
        }
        context.registerToy(toy);
    }
}

function handleToySnapshot(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    if (!context.headlessmode) {
        const toys = e.data?.toys;
        if (Array.isArray(toys)) {
            for (const toy of toys) {
                if (toy && typeof toy.networkId === 'number') {
                    context.registerToy(toy);
                }
            }
        }
    }
}

function handleToyAllDeleted(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    if (!context.headlessmode) {
        const ids = Array.isArray(e.data?.networkIds) ? e.data.networkIds : undefined;
        context.removeAllToys(ids);
    }
}

function handlePlayerSpeakChange(context: EventHandlerContext, e: WonderlandWebsocketEvent, remoteUser: any): void {
    const playerId = e.data.playerId;
    const isSpeaking = e.data.isSpeaking;
    if (!context.headlessmode && playerId !== context.playerUserId) {
        const indicatorObject = remoteUser?.userIndicatorObject?.object;
        const indicator = indicatorObject?.getComponent(SpeakerIndicator);
        if (indicator) {
            indicator.setState(isSpeaking ? SpeakerState.Speaking : SpeakerState.NotSpeaking);
        }
    }
}

function handlePlayerSharingStreamOffer(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    Logger.debug('Received sharing offer from sender: ' + e.data.id, MODULE);
    context.handleVidshareOffer(e.data.id, e.data.description);
}

function handleMarketplaceTipReceived(e: WonderlandWebsocketEvent): void {
    const amount = Number(e.data?.amount ?? 0);
    const senderLabel = String(e.data?.from_label || e.data?.from_display_name || e.data?.from_username || 'Someone');
    const roomName = String(e.data?.room || '').trim();

    if (!Number.isFinite(amount) || amount <= 0) {
        return;
    }

    const roomSuffix = roomName ? ` in room ${roomName}` : '';
    UserMessaging.instance.notify(`You received a ${amount}-token tip from ${senderLabel}${roomSuffix}.`, 8.0);
}

function handlePlayerSharingStreamOfferICE(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    Logger.debug('Received ICE candidate from sender: ' + e.data.id, MODULE);
    if (e.data && e.data.candidate) {
        if (context.vidrecPeerConnections.get(e.data.id)?.remoteDescription) {
            context.vidrecPeerConnections
                .get(e.data.id)
                ?.addIceCandidate(e.data.candidate)
                .then((val) => Logger.debug('Added ICE candidate for vid sharing', MODULE, val))
                .catch((err) => Logger.warn('Could not add ICE candidate for vid sharing', MODULE, err));
        } else {
            if (context.vidrecCandidatesMap.has(e.data.id)) {
                context.vidrecCandidatesMap.get(e.data.id)?.push(e.data.candidate);
            } else {
                context.vidrecCandidatesMap.set(e.data.id, [e.data.candidate]);
            }
        }
    }
}

function handlePlayerSharingStreamAnswerICE(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    if (context.sharingVideo) {
        Logger.debug('Received answer ICE candidate from receiver: ' + e.data.id, MODULE);

        if (e.data && e.data.candidate) {
            if (context.vidsharePeerConnections.get(e.data.id)?.remoteDescription) {
                context.vidsharePeerConnections
                    .get(e.data.id)
                    ?.addIceCandidate(e.data.candidate)
                    .then((val) => Logger.debug('Added ICE candidate for vid sharing', MODULE, val))
                    .catch((err) => Logger.warn('Could not add ICE candidate for vid sharing', MODULE, err));
            } else {
                if (!context.vidshareCandidatesMap.has(e.data.id)) {
                    context.vidshareCandidatesMap.set(e.data.id, [e.data.candidate]);
                } else {
                    context.vidshareCandidatesMap.get(e.data.id)?.push(e.data.candidate);
                }
            }
        }
    }
}

function handlePlayerSharingStreamAnswer(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    if (context.sharingVideo) {
        Logger.debug('Received vid share answer from receiver: ' + e.data.id, MODULE);
        if (
            context.vidsharePeerConnections.has(e.data.id) &&
            context.vidsharePeerConnections.get(e.data.id)?.localDescription
        ) {
            Logger.debug('Setting remote description to received answer', MODULE);
            context.vidsharePeerConnections.get(e.data.id)?.setRemoteDescription(e.data.description);
        }
    }
}

function handlePlayerClaimingMasterShareRole(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    Logger.info('Master sharer is now: ' + e.data.id, MODULE);
    context.onClaimMasterShareEvent.notify(e.data.id);
    context.masterSharerId = e.data.id;
}

function handlePlayerReleasingMasterShareRole(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    Logger.info('Master sharer stopped sharing: ' + e.data.id, MODULE);
    context.activeVideoStreams.delete(e.data.id);
    context.onShareStopEvent.notify(e.data.id);
    context.masterSharerId = 0;
}

function handlePlayerStoppedSharingStream(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    Logger.info('Peer sharer stopped sharing: ' + e.data.id, MODULE);
    context.activeVideoStreams.delete(e.data.id);
    context.onShareStopEvent.notify(e.data.id);
}

function handlePlayerSyncUrlVideoStart(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    const url = typeof e.data?.url === 'string' ? e.data.url.trim() : '';
    const startAtMsRaw = Number(e.data?.startAtMs);
    const requestedByRaw = Number(e.data?.requestedBy);
    const seekSecondsRaw = Number(e.data?.seekSeconds);

    if (!url || !Number.isFinite(startAtMsRaw)) {
        Logger.warn('Invalid PLAYER_SYNC_URL_VIDEO_START payload', MODULE, e.data);
        return;
    }

    const share = {
        url,
        startAtMs: Math.round(startAtMsRaw),
        requestedBy: Number.isFinite(requestedByRaw) ? requestedByRaw : 0,
        seekSeconds: Number.isFinite(seekSecondsRaw) ? Math.max(0, seekSecondsRaw) : 0,
    };
    context.activeUrlVideoShare = share;
    context.onSyncUrlVideoStartEvent.notify(share);
}

function handlePlayerSyncUrlVideoPing(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    const url = typeof e.data?.url === 'string' ? e.data.url.trim() : '';
    const startAtMsRaw = Number(e.data?.startAtMs);
    const requestedByRaw = Number(e.data?.requestedBy);
    const seekSecondsRaw = Number(e.data?.seekSeconds);

    if (!url || !Number.isFinite(startAtMsRaw)) {
        Logger.warn('Invalid PLAYER_SYNC_URL_VIDEO_PING payload', MODULE, e.data);
        return;
    }

    const share = {
        url,
        startAtMs: Math.round(startAtMsRaw),
        requestedBy: Number.isFinite(requestedByRaw) ? requestedByRaw : 0,
        seekSeconds: Number.isFinite(seekSecondsRaw) ? Math.max(0, seekSecondsRaw) : 0,
    };
    // Pings carry the authoritative clock, so a surface that starts later can still catch up.
    context.activeUrlVideoShare = share;
    context.onSyncUrlVideoPingEvent.notify(share);
}

function handleServerPing(e: WonderlandWebsocketEvent): void {
    if (networkManager && networkManager.client) {
        networkManager.client.sendViaWs({
            type: Events.CLIENT_PONG,
            data: { timestamp: e.data.timestamp },
        });
    }
}

function handleSetId(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    Logger.debug('Set own network ID to: ' + e.data.id, MODULE);
    context.playerUserId = e.data.id;
    context.serverAssignedUserId = e.data.id;
    context._serverNetworkId = e.data.serverNetworkId;
}

function handleProstheticStateUpdate(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    const userId = Number(e.data?.id);
    if (!Number.isFinite(userId) || userId <= 0) {
        Logger.warn('Invalid PROSTHETIC_STATE_UPDATE payload: missing user id', MODULE, e.data);
        return;
    }

    const state: ProstheticState = {
        type: e.data?.type,
        networkId: typeof e.data?.networkId === 'number' ? e.data.networkId : null,
    };
    const deactivatedNetworkId = typeof e.data?.deactivatedNetworkId === 'number'
        ? e.data.deactivatedNetworkId
        : null;

    if (userId === context.playerUserId) {
        context.applyLocalProstheticState(state);
        return;
    }

    const cachedJoin = context.remoteUserJoinData.get(userId);
    if (cachedJoin) {
        cachedJoin.prostheticState = {
            type: state.type,
            networkId: state.networkId,
        };
        context.remoteUserJoinData.set(userId, cachedJoin);
    }

    context.applyRemoteProstheticState(userId, state, deactivatedNetworkId);
}

function handleSceneSwitchResponse(e: WonderlandWebsocketEvent): void {
    const accepted = Boolean(e.data?.accepted);
    const reason = typeof e.data?.reason === 'string' ? e.data.reason : '';
    const sceneRef = typeof e.data?.sceneRef === 'string' ? e.data.sceneRef : '';

    if (accepted) {
        UserMessaging.instance.notify(`Scene switch approved: ${sceneRef}`);
        EventsClient.instance.clearSceneSwitchCooldown();
    } else {
        const cooldownMatch = reason.match(/(\d+)s/i);
        const cooldownSeconds = cooldownMatch ? Number(cooldownMatch[1]) : 0;
        if (cooldownSeconds > 0) {
            EventsClient.instance.setSceneSwitchCooldownSeconds(cooldownSeconds);
        }

        const detail = reason ? ` (${reason})` : '';
        UserMessaging.instance.notify(`Scene switch rejected${detail}`);
    }
}

function handleSceneSwitchApproved(context: EventHandlerContext, e: WonderlandWebsocketEvent): void {
    if (context.headlessmode) {
        return;
    }

    const sceneRef = typeof e.data?.sceneRef === 'string' ? e.data.sceneRef : '';
    const requesterId = typeof e.data?.requestedBy === 'number' ? e.data.requestedBy 
        : typeof e.data?.requesterId === 'number' ? e.data.requesterId 
        : typeof e.data?.playerId === 'number' ? e.data.playerId
        : typeof e.data?.userId === 'number' ? e.data.userId
        : undefined;

    if (!sceneRef) {
        Logger.warn('SCENE_SWITCH_APPROVED received without valid sceneRef', MODULE);
        return;
    }

    const requesterName = (requesterId !== undefined && requesterId !== -1)
        ? (context.remoteUsernames.get(requesterId) ?? 
           (requesterId === context.playerUserId ? 'You' : `Player ${requesterId}`))
        : 'Server';

    const sceneName = sceneRef === 'default' ? 'Default Scene' : sceneRef;
    UserMessaging.instance.notifyState(`Scene change: ${sceneName}\n (requested by ${requesterName})`);

    EventsClient.instance.clearSceneSwitchCooldown();

    const sceneController = SceneController.instance;
    if (!sceneController) {
        Logger.error('SceneController singleton not available for scene switch approval', MODULE);
        return;
    }

    // Handle "default" sceneRef - load scene at index 0
    const sceneToLoad = sceneRef === 'default' ? 0 : sceneRef;

    void sceneController.switchToScene(sceneToLoad)
        .then(() => {
            // Always re-create all remote user avatars after a scene switch.
            // sceneRoot.destroy() may have destroyed avatar clones, and even if they
            // survived (clone(null)), they need to be freshly placed in the new scene context.
            for (const [userId, cachedData] of context.remoteUserJoinData) {
                if (cachedData.headlessmode) continue;
                Logger.info(`Re-creating avatar for user ${userId} after scene switch`, MODULE);
                const staleEntry = context.remoteUsers.get(userId);
                handleUserJoined(context, { type: Events.USER_JOINED, data: cachedData } as WonderlandWebsocketEvent, staleEntry);
            }
        })
        .catch((error) => {
            Logger.error(`Failed to switch to approved scene '${sceneRef}'`, MODULE, error);
        });
}
